export type AuthSession = {
  userId?: string;
};

const STORAGE_KEY = "auth_session";
const AUTH_SESSION_EVENT = "auth-session:changed";

const isBrowser = typeof window !== "undefined" && !!window.localStorage;

const readSession = (): AuthSession | null => {
  if (!isBrowser) {
    return null;
  }

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as AuthSession;
    if (parsed && typeof parsed === "object") {
      return parsed;
    }
  } catch (error) {
    // ignore parse error and fallthrough to cleanup
  }

  window.localStorage.removeItem(STORAGE_KEY);
  return null;
};

const notify = () => {
  if (!isBrowser) {
    return;
  }

  window.dispatchEvent(new Event(AUTH_SESSION_EVENT));
};

export const getAuthSession = (): AuthSession | null => readSession();

export const hasAuthSession = (): boolean => !!readSession();

export const setAuthSession = (session: AuthSession) => {
  if (!isBrowser) {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    // ignore storage errors (e.g. private mode quota)
  }

  notify();
};

export const clearAuthSession = () => {
  if (!isBrowser) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // ignore storage errors
  }

  notify();
};

export const subscribeAuthSession = (listener: () => void) => {
  if (!isBrowser) {
    return () => undefined;
  }

  const handleCustom = () => listener();
  const handleStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) {
      listener();
    }
  };

  window.addEventListener(AUTH_SESSION_EVENT, handleCustom);
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener(AUTH_SESSION_EVENT, handleCustom);
    window.removeEventListener("storage", handleStorage);
  };
};
